import { useMemo } from 'react';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';

import {
  formatSelectedDates,
  formatWeekDays,
  getRangeEndDate,
} from '../components/RevampServicesCta/utils';
import { expandRangeToDates, formatLooseDates } from '../utils/looseDates';

import { useRevampCta } from './useRevampCta';
import { useSelectedLooseDates, useSelectedRecurrence } from './useUpdateSelectedDates';

type RevampSelectedDates = {
  allowsLooseDates: boolean;
  allowsRecurring: boolean;
  looseDates: Date[];
  isRecurring: boolean;
  weekDays: number[];
  hasDates: boolean;
  datesLabel: string;
};

/**
 * The dates currently applied to the page as the CTA displays them. Daytime services
 * book their days one by one, so they read the selection from `dates` and treat a
 * range arriving from search as every day it covers. A service billed weekly can
 * instead repeat, which the CTA reads as a set of weekdays and a day to start from.
 */
export const useRevampSelectedDates = (
  locale: Locale,
  selectedDate: Date,
  selectedEndDate?: Date,
  hasDateRange?: boolean
): RevampSelectedDates => {
  const { selectedService } = useRevampCta();
  const looseDatesFromUrl = useSelectedLooseDates();
  const { isRecurring: isRecurringInUrl, weekDays } = useSelectedRecurrence();

  const allowsLooseDates = selectedService?.serviceType.supportsNonContiguousDates === true;
  const allowsRecurring = selectedService?.serviceType.supportsRecurringBilling === true;
  const isRecurring = allowsRecurring && isRecurringInUrl;

  // The picker restarts from these days every time it opens, so they have to keep
  // their identity across renders.
  const looseDates = useMemo(() => {
    if (!allowsLooseDates || isRecurring) return [];
    if (looseDatesFromUrl.length > 0) return looseDatesFromUrl;
    return expandRangeToDates(
      hasDateRange ? selectedDate : undefined,
      getRangeEndDate(selectedDate, selectedEndDate)
    );
  }, [
    allowsLooseDates,
    isRecurring,
    looseDatesFromUrl,
    hasDateRange,
    selectedDate,
    selectedEndDate,
  ]);

  const common = { allowsLooseDates, allowsRecurring, looseDates, isRecurring, weekDays };

  if (isRecurring) {
    const startDateLabel = formatSelectedDates(locale, selectedDate);
    const weekDaysLabel = formatWeekDays(locale, weekDays);
    return {
      ...common,
      hasDates: weekDays.length > 0 && !!hasDateRange,
      datesLabel:
        weekDays.length > 0 && hasDateRange ? `Weekly ${weekDaysLabel} from ${startDateLabel}` : '',
    };
  }

  if (!allowsLooseDates) {
    return {
      ...common,
      hasDates: !!hasDateRange,
      datesLabel: hasDateRange ? formatSelectedDates(locale, selectedDate, selectedEndDate) : '',
    };
  }

  return {
    ...common,
    hasDates: looseDates.length > 0,
    datesLabel: looseDates.length > 0 ? formatLooseDates(locale, looseDates) : '',
  };
};

export default useRevampSelectedDates;
